import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatMessageList } from '@/components/chat/ChatMessageList'
import { ChatSettingsPanel } from '@/components/chat/ChatSettingsPanel'
import {
  ChatSettingsSheet,
  CollapsibleSettings,
} from '@/components/chat/ChatSettingsSheet'
import { ChatTabBar, ChatTabPicker } from '@/components/chat/ChatTabBar'
import { useChatLayoutMode } from '@/hooks/useChatLayoutMode'
import { sessionApi } from '@/lib/api/sessions'
import { compareApi } from '@/lib/api/compare'
import { useMockApi } from '@/lib/api/client'
import { buildSettingsBadge } from '@/lib/chat/settings-badge'
import {
  abortCompareStream,
  finishCompareStream,
  initCompareStream,
  updateCompareStream,
} from '@/lib/compare/stream-store'
import type { ChatMessage, SessionSettings } from '@/types/chat'
import { useChatWorkspaceStore } from '@/stores/chatWorkspaceStore'
import { Button } from '@/components/ui/button'

export function ChatWorkspaceLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const layoutMode = useChatLayoutMode()
  const [sending, setSending] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [loading, setLoading] = useState(true)
  const [sendError, setSendError] = useState<string | null>(null)
  const chatAbortRef = useRef<AbortController | null>(null)
  const compareAbortRef = useRef<AbortController | null>(null)

  const {
    sessions,
    activeSessionId,
    compareMode,
    compareSelection,
    settingsOpen,
    settingsSheetOpen,
    settingsCollapsed,
    draftPrompt,
    setSessions,
    upsertSession,
    removeOpenTab,
    setActiveSessionId,
    setCompareMode,
    toggleCompareSelection,
    clearCompareSelection,
    setSettingsOpen,
    setSettingsSheetOpen,
    setSettingsCollapsed,
    setDraftPrompt,
    updateLocalSettings,
    updateLocalSystemPrompt,
    addSessionMessage,
    patchSessionMessage,
  } = useChatWorkspaceStore()

  useEffect(() => {
    let cancelled = false

    sessionApi
      .listSessions({ status: 'open' })
      .then(async (result) => {
        if (cancelled) return

        if (useMockApi()) {
          setSessions(result.items)
          setLoading(false)
          return
        }

        const details = await Promise.all(
          result.items.map(async (item) => (await sessionApi.getSession(item.id)) ?? item),
        )
        setSessions(details)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [setSessions])

  useEffect(() => {
    const openSessionId = (location.state as { openSessionId?: string } | null)?.openSessionId
    if (!openSessionId || loading) return

    let cancelled = false

    sessionApi
      .getSession(openSessionId)
      .then((session) => {
        if (cancelled || !session) return
        upsertSession(session)
        navigate('/chat', { replace: true, state: null })
      })
      .catch(() => {
        if (!cancelled) navigate('/chat', { replace: true, state: null })
      })

    return () => {
      cancelled = true
    }
  }, [loading, location.state, navigate, upsertSession])

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) ?? null,
    [sessions, activeSessionId],
  )

  const handleCloseTab = useCallback(
    async (sessionId: string) => {
      await sessionApi.closeSession(sessionId)
      removeOpenTab(sessionId)
    },
    [removeOpenTab],
  )

  const handleSend = useCallback(async () => {
    if (!activeSession || !draftPrompt.trim()) return

    if (compareMode) {
      if (compareSelection.length < 2) return

      setSending(true)
      const promptText = draftPrompt.trim()
      const title = `Compare: ${promptText.slice(0, 40)}`
      let batchId = ''
      const abortCtrl = new AbortController()
      compareAbortRef.current = abortCtrl

      try {
        await compareApi.sendCompareStream(
          {
            sessionIds: compareSelection,
            promptText,
            title,
            settingsOverride: activeSession.defaultSettings,
            signal: abortCtrl.signal,
          },
          {
            onMeta: ({ batch }) => {
              batchId = batch.id
              initCompareStream(batch, () => abortCtrl.abort())
              setDraftPrompt('')
              clearCompareSelection()
              setCompareMode(false)
              navigate(`/compare/${batch.id}`)
            },
            onChunk: (modelResponseId, delta) => {
              updateCompareStream(batchId, (batch) => ({
                ...batch,
                responses: batch.responses.map((response) =>
                  response.modelResponseId === modelResponseId
                    ? {
                        ...response,
                        status: 'streaming',
                        normalizedText: `${response.normalizedText ?? ''}${delta}`,
                      }
                    : response,
                ),
              }))
            },
            onResponseDone: (result) => {
              updateCompareStream(batchId, (batch) => ({
                ...batch,
                responses: batch.responses.map((response) =>
                  response.modelResponseId === result.modelResponseId
                    ? {
                        ...response,
                        normalizedText: result.normalizedText,
                        tokensInput: result.tokensInput,
                        tokensOutput: result.tokensOutput,
                        estimatedCost: result.estimatedCost,
                        latencyMs: result.latencyMs,
                        status: result.status,
                        errorMessage: result.errorMessage,
                      }
                    : response,
                ),
              }))
            },
            onDone: (completedBatchId) => {
              finishCompareStream(completedBatchId)
            },
            onError: () => {
              if (batchId) finishCompareStream(batchId)
            },
          },
        )
      } catch {
        if (batchId) abortCompareStream(batchId)
      } finally {
        compareAbortRef.current = null
        setSending(false)
      }
      return
    }

    setSending(true)
    setStreaming(true)
    setSendError(null)

    const promptText = draftPrompt.trim()
    const settingsSnapshot = activeSession.defaultSettings
    const pendingMessageId = `pending-${crypto.randomUUID()}`
    let messageKey = pendingMessageId
    const createdAt = new Date().toISOString()
    const abortCtrl = new AbortController()
    chatAbortRef.current = abortCtrl

    addSessionMessage(activeSession.id, {
      id: pendingMessageId,
      promptRequestId: pendingMessageId,
      promptText,
      settingsSnapshot,
      settingsBadge: buildSettingsBadge(settingsSnapshot),
      response: {
        id: `pending-resp-${crypto.randomUUID()}`,
        normalizedText: '',
        tokensInput: 0,
        tokensOutput: 0,
        estimatedCost: 0,
        latencyMs: 0,
        status: 'streaming',
      },
      createdAt,
    })
    setDraftPrompt('')

    try {
      await sessionApi.sendMessageStream(
        activeSession.id,
        promptText,
        activeSession.defaultSettings,
        {
          onMeta: ({ requestId, responseId }) => {
            patchSessionMessage(activeSession.id, messageKey, (message) => ({
              ...message,
              id: requestId,
              promptRequestId: requestId,
              response: message.response
                ? { ...message.response, id: responseId }
                : undefined,
            }))
            messageKey = requestId
          },
          onChunk: (delta) => {
            patchSessionMessage(activeSession.id, messageKey, (message) => ({
              ...message,
              response: message.response
                ? {
                    ...message.response,
                    normalizedText: `${message.response.normalizedText}${delta}`,
                    status: 'streaming',
                  }
                : undefined,
            }))
          },
          onDone: (result) => {
            patchSessionMessage(activeSession.id, messageKey, (message) => ({
              ...message,
              id: result.requestId,
              promptRequestId: result.requestId,
              response: {
                id: result.responseId,
                normalizedText: result.normalizedText,
                tokensInput: result.tokensInput,
                tokensOutput: result.tokensOutput,
                estimatedCost: result.estimatedCost,
                latencyMs: result.latencyMs,
                status: result.status,
              },
            }))
          },
          onError: (message) => {
            setSendError(message)
          },
          onCancelled: (responseId) => {
            patchSessionMessage(activeSession.id, messageKey, (message) => ({
              ...message,
              response: message.response
                ? { ...message.response, id: responseId ?? message.response.id, status: 'cancelled' }
                : undefined,
            }))
          },
        },
        abortCtrl.signal,
      )

      const refreshed = await sessionApi.getSession(activeSession.id)
      if (refreshed) upsertSession(refreshed)
    } catch {
      // aborted or network error — refresh to get saved partial state
      const refreshed = await sessionApi.getSession(activeSession.id)
      if (refreshed) upsertSession(refreshed)
    } finally {
      chatAbortRef.current = null
      setSending(false)
      setStreaming(false)
    }
  }, [
    activeSession,
    addSessionMessage,
    patchSessionMessage,
    compareMode,
    compareSelection,
    draftPrompt,
    navigate,
    setDraftPrompt,
    upsertSession,
    clearCompareSelection,
    setCompareMode,
  ])

  const handleStop = useCallback(() => {
    chatAbortRef.current?.abort()
    compareAbortRef.current?.abort()
  }, [])

  const persistSettings = useCallback(
    async (
      sessionId: string,
      patch: { systemPrompt?: string; defaultSettings?: SessionSettings },
    ) => {
      const updated = await sessionApi.updateSession(sessionId, patch)
      upsertSession(updated)
    },
    [upsertSession],
  )

  const handleSettingsChange = (patch: Partial<SessionSettings>) => {
    if (!activeSession) return
    updateLocalSettings(activeSession.id, patch)
    void persistSettings(activeSession.id, {
      defaultSettings: { ...activeSession.defaultSettings, ...patch },
    })
  }

  const handleSystemPromptChange = (value: string) => {
    if (!activeSession) return
    updateLocalSystemPrompt(activeSession.id, value)
  }

  const handleSystemPromptBlur = () => {
    if (!activeSession) return
    void persistSettings(activeSession.id, { systemPrompt: activeSession.systemPrompt ?? '' })
  }

  if (loading) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading chat...</div>
  }

  if (!activeSession) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 p-6 text-sm text-muted-foreground"
        data-testid="chat-empty-state"
      >
        <p>No open sessions. Start a chat from the Catalog.</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/catalog" data-testid="chat-empty-go-catalog">
            Go to Catalog
          </Link>
        </Button>
      </div>
    )
  }

  const useTextareaFallback = layoutMode === 'phone-portrait'
  const showSettingsSidebar = layoutMode === 'desktop' && settingsOpen
  const showVerticalTabs = layoutMode === 'tablet-landscape'
  const showHorizontalTabs = layoutMode === 'desktop' || layoutMode === 'tablet-portrait'
  const showTabPicker = layoutMode === 'phone-portrait' || layoutMode === 'phone-landscape'
  const showSettingsSheet = layoutMode === 'phone-portrait'
  const showCollapsibleSettings =
    layoutMode === 'phone-landscape' || layoutMode === 'tablet-portrait' || layoutMode === 'tablet-landscape'

  const settingsPanel = (
    <ChatSettingsPanel
      settings={activeSession.defaultSettings}
      systemPrompt={activeSession.systemPrompt ?? ''}
      onSettingsChange={handleSettingsChange}
      onSystemPromptChange={handleSystemPromptChange}
      onSystemPromptBlur={handleSystemPromptBlur}
      useTextareaFallback={useTextareaFallback}
      className="border-0"
    />
  )

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-workspace">
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
        {showTabPicker ? (
          <ChatTabPicker
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={setActiveSessionId}
            compareMode={compareMode}
            compareSelection={compareSelection}
            onToggleCompare={toggleCompareSelection}
          />
        ) : null}
        {showHorizontalTabs ? (
          <ChatTabBar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={setActiveSessionId}
            onClose={handleCloseTab}
            compareMode={compareMode}
            compareSelection={compareSelection}
            onToggleCompare={toggleCompareSelection}
            className="flex-1 border-0"
          />
        ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={compareMode ? 'default' : 'outline'}
            size="sm"
            className="min-h-11 shrink-0"
            onClick={() => setCompareMode(!compareMode)}
            data-testid="chat-compare-mode-toggle"
          >
            Compare
          </Button>
        {layoutMode === 'desktop' ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(!settingsOpen)}
            data-testid="chat-settings-sidebar-toggle"
          >
            Settings
          </Button>
        ) : null}
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {showVerticalTabs ? (
          <ChatTabBar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={setActiveSessionId}
            onClose={handleCloseTab}
            compareMode={compareMode}
            compareSelection={compareSelection}
            onToggleCompare={toggleCompareSelection}
            orientation="vertical"
          />
        ) : null}

        {showSettingsSidebar ? settingsPanel : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {showCollapsibleSettings ? (
            <CollapsibleSettings
              collapsed={settingsCollapsed}
              onToggle={() => setSettingsCollapsed(!settingsCollapsed)}
            >
              <div onBlur={handleSystemPromptBlur}>{settingsPanel}</div>
            </CollapsibleSettings>
          ) : null}

          <ChatMessageList messages={activeSession.messages} />

          {sendError ? (
            <p className="border-t border-border px-4 py-2 text-sm text-destructive" data-testid="chat-send-error">
              {sendError}
            </p>
          ) : null}

          <ChatComposer
            value={draftPrompt}
            onChange={setDraftPrompt}
            onSend={handleSend}
            onStop={handleStop}
            streaming={streaming}
            disabled={sending || (compareMode && compareSelection.length < 2)}
            sendLabel={
              compareMode
                ? `Send to selected (${compareSelection.length})`
                : undefined
            }
            showSettingsButton={showSettingsSheet}
            onOpenSettings={() => setSettingsSheetOpen(true)}
          />
        </div>
      </div>

      {showSettingsSheet ? (
        <ChatSettingsSheet
          open={settingsSheetOpen}
          onClose={() => {
            setSettingsSheetOpen(false)
            void handleSystemPromptBlur()
          }}
          settings={activeSession.defaultSettings}
          systemPrompt={activeSession.systemPrompt ?? ''}
          onSettingsChange={handleSettingsChange}
          onSystemPromptChange={handleSystemPromptChange}
        />
      ) : null}
    </div>
  )
}
